import React, { useMemo } from 'react';
import { AppAdminFormLabel, AppButton, AppMultiSelect } from '@/components/atoms';
import { Search, Download } from '@/components/atoms/AppIcon';
import { SearchTextFieldWithLabel } from '@/components/molecules';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { TenantSiteTypeItem } from '@/services/tenants';

type DistanceCachesFilterPanelProps = {
  tenants: TenantSiteTypeItem[];
  showSiteFilter: boolean;
  siteTypes: string[];
  hasAllSelected: boolean;
  onChangeSiteTypes: (next: string[]) => void;
  clinicNumber: string;
  onChangeClinicNumber: (next: string) => void;
  address1: string;
  onChangeAddress1: (next: string) => void;
  address2: string;
  onChangeAddress2: (next: string) => void;
  onSearch: () => void;
  onCsv: () => void;
  csvLoading?: boolean;
};

const DistanceCachesFilterPanel: React.FC<DistanceCachesFilterPanelProps> = ({
  tenants,
  showSiteFilter,
  siteTypes,
  hasAllSelected,
  onChangeSiteTypes,
  clinicNumber,
  onChangeClinicNumber,
  address1,
  onChangeAddress1,
  address2,
  onChangeAddress2,
  onSearch,
  onCsv,
  csvLoading = false,
}) => {
  const { colors } = useTheme();

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.stopPropagation();
    onSearch();
  };

  const tenantSiteTypes = useMemo(
    () => tenants.map((t) => t.site_type).filter(Boolean) as string[],
    [tenants]
  );

  const siteTypeOptions = [
    { label: 'すべて', value: 'all' },
    ...tenants.map((t) => ({ label: t.name, value: t.site_type })),
  ];

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      <div className="flex gap-4 items-end">
        {showSiteFilter && (
          <div className="flex-1 min-w-0 pb-7">
            <AppAdminFormLabel label="サイト区分" />
            <AppMultiSelect
              id="distance_cache_site_type"
              placeholder="サイト区分"
              value={siteTypes}
              style={{ width: '100%' }}
              maxTagCount={hasAllSelected ? 0 : 'responsive'}
              maxTagPlaceholder={hasAllSelected ? () => 'すべて' : undefined}
              showSearch
              onInputKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              options={siteTypeOptions}
              onChange={(values) => {
                const next = (values as string[]) || [];
                if (next.includes('all')) {
                  onChangeSiteTypes(tenantSiteTypes);
                  return;
                }
                onChangeSiteTypes(next.filter((v) => v !== 'all'));
              }}
            />
          </div>
        )}
        <SearchTextFieldWithLabel
          className="flex-1 min-w-0"
          label="治療院ID"
          id="distance_cache_clinic_number"
          value={clinicNumber}
          placeholder="治療院ID"
          onChange={(e) => onChangeClinicNumber(e.target.value)}
          onKeyDown={handleEnterSearch}
        />
        <SearchTextFieldWithLabel
          className="flex-1 min-w-0"
          label="From住所"
          id="distance_cache_address1"
          value={address1}
          placeholder="From住所"
          onChange={(e) => onChangeAddress1(e.target.value)}
          onKeyDown={handleEnterSearch}
        />
        <SearchTextFieldWithLabel
          className="flex-1 min-w-0"
          label="To住所"
          id="distance_cache_address2"
          value={address2}
          placeholder="To住所"
          onChange={(e) => onChangeAddress2(e.target.value)}
          onKeyDown={handleEnterSearch}
        />
        <div className="flex gap-2 items-center shrink-0 pb-7">
          <AppButton
            onClick={onSearch}
            type="primary"
            icon={<Search />}
            style={{ backgroundColor: colors.secondary, borderColor: colors.secondary }}
          >
            検索
          </AppButton>
          <AppButton
            onClick={onCsv}
            type="primary"
            icon={<Download />}
            style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
            className="btn-ra2-hover"
            loading={csvLoading}
          >
            CSV
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default DistanceCachesFilterPanel;
